import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '@/i18n';
import { Globe } from 'lucide-react';

export const LanguageSelector = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    document.documentElement.dir = LANGUAGES.find(l => l.code === code)?.rtl ? 'rtl' : 'ltr';
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 transition-colors ${
          variant === 'compact'
            ? 'p-2 text-muted-foreground hover:text-primary'
            : 'px-3 py-1.5 text-sm font-mono text-muted-foreground hover:text-primary border border-white/10 rounded hover:border-primary/30'
        }`}
        data-testid="language-selector-trigger"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[199] sm:hidden" onClick={() => setOpen(false)} />
          <div className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-full sm:mt-2 w-48 bg-card border border-border rounded-lg shadow-xl z-[200] max-h-64 overflow-y-auto" data-testid="language-dropdown">
            <div className="p-2 border-b border-border flex items-center justify-between">
              <span className="text-xs font-mono text-muted-foreground px-1">LANGUAGE</span>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground p-0.5 sm:hidden">
                <span className="text-xs">✕</span>
              </button>
            </div>
            <div className="py-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLang(lang.code)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 transition-colors ${
                    i18n.language === lang.code
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-foreground hover:bg-secondary'
                  }`}
                  data-testid={`lang-option-${lang.code}`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span>{lang.name}</span>
                  {i18n.language === lang.code && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-primary" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
